import React, { useState } from "react";
import { toast } from "sonner";
import { X, KeyRound, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm(""); setShow(false);
  };

  const close = () => { if (!saving) { reset(); onClose(); } };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      reset();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm overflow-y-auto" onClick={close}>
      <div className="min-h-full flex items-end sm:items-center justify-center p-4">
        <div
          data-testid="change-password-dialog"
          className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl my-8"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-sky text-white flex items-center justify-center">
              <KeyRound className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-ink">Change password</h2>
          </div>
          <button onClick={close} disabled={saving} className="text-stone hover:text-ink">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Current password</label>
            <input
              data-testid="cp-current"
              type={show ? "text" : "password"}
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full h-11 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-semibold"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">New password</label>
            <input
              data-testid="cp-new"
              type={show ? "text" : "password"}
              required
              minLength={6}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="At least 6 characters"
              className="w-full h-11 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-semibold"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">Confirm new password</label>
            <input
              data-testid="cp-confirm"
              type={show ? "text" : "password"}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full h-11 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-semibold"
            />
          </div>

          <button
            type="button"
            onClick={() => setShow(s => !s)}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-hover hover:underline"
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {show ? "Hide passwords" : "Show passwords"}
          </button>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={close} disabled={saving} className="px-4 py-2 rounded-full text-stone font-bold">Cancel</button>
            <button
              type="submit"
              data-testid="cp-submit"
              disabled={saving}
              className="px-5 py-2 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
            >
              {saving ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
